import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus, Trash2, Pencil, ExternalLink, Globe2, NotebookText, RefreshCcw,
  TrendingUp, TrendingDown, Minus, GripVertical, Search,
} from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import BUBriefEditor from "@/components/BUBriefEditor";
import BUSeoEditor from "@/components/BUSeoEditor";
import PropertyPlatformsEditor, { PlatformPills } from "@/components/PropertyPlatformsEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function PropertiesPage() {
  const { clientId } = useParams();
  const [items, setItems] = useState([]);
  const [summaries, setSummaries] = useState({}); // bu_id -> {budget_total, actual_total, visits, visits_pct_change, period_month}
  const [domainsByBu, setDomainsByBu] = useState({});
  const [editing, setEditing] = useState(null);
  const [briefBuId, setBriefBuId] = useState(null);
  const [platformsBuId, setPlatformsBuId] = useState(null);
  const [seoBuId, setSeoBuId] = useState(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  // Default priority = next free slot. Recomputed every time the user opens
  // the add-form fresh (i.e. not editing) so the very first property defaults
  // to "1", the second to "2", etc. Capped at 10 (UI limit).
  //
  // We avoid storing the auto-calculated default in state — instead we keep a
  // `priorityOverride` which is non-null only when the user has typed an
  // explicit value. The displayed value is then `override ?? nextSlot`.
  const [priorityOverride, setPriorityOverride] = useState(null);
  const nextPriority = () => Math.min((items?.length || 0) + 1, 10);
  const priority = priorityOverride ?? nextPriority();
  const setPriority = setPriorityOverride;
  const [busy, setBusy] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/clients/${clientId}/business-units`);
    // Sort by priority (1 = most important) then name
    data.sort((a, b) => (a.priority || 5) - (b.priority || 5) || a.name.localeCompare(b.name));
    setItems(data);
    // Fetch budget summaries in parallel
    const sums = await Promise.all(
      data.map((b) =>
        api.get(`/business-units/${b.business_unit_id}/budget-summary`)
          .then((r) => [b.business_unit_id, r.data])
          .catch(() => [b.business_unit_id, null])
      )
    );
    const map = {};
    sums.forEach(([id, s]) => { if (s) map[id] = s; });
    setSummaries(map);
  }, [clientId]);

  const persistOrder = async (orderedItems) => {
    setSavingOrder(true);
    try {
      const order = orderedItems.map((b) => b.business_unit_id);
      await api.post(`/clients/${clientId}/business-units/reorder`, { order });
      toast.success("Order saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
      load();
    } finally {
      setSavingOrder(false);
    }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox; setData throws on a few legacy browsers — safe to ignore
    try {
      e.dataTransfer.setData("text/plain", id);
    } catch (_dragErr) {
      // no-op: legacy browser without setData support
    }
  };

  const onDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };

  const onDragLeave = () => setOverId(null);

  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    setOverId(null);
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const fromIdx = items.findIndex((b) => b.business_unit_id === dragId);
    const toIdx = items.findIndex((b) => b.business_unit_id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); return; }
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Optimistic: update priorities locally so the UI feels instant
    setItems(next.map((b, i) => ({ ...b, priority: Math.min(i + 1, 10) })));
    setDragId(null);
    persistOrder(next);
  };

  const onDragEnd = () => { setDragId(null); setOverId(null); };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/clients/${clientId}/domains`).then((r) => {
      const map = {};
      (r.data?.domains || []).forEach((d) => {
        if (!d.business_unit_id) return;
        (map[d.business_unit_id] = map[d.business_unit_id] || []).push(d);
      });
      setDomainsByBu(map);
    }).catch(() => setDomainsByBu({}));
  }, [clientId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name, website_url: url, priority: Number(priority) || nextPriority() };
      if (editing) {
        await api.patch(`/business-units/${editing.business_unit_id}`, payload);
        toast.success("Property updated");
      } else {
        await api.post(`/clients/${clientId}/business-units`, payload);
        toast.success("Property added");
      }
      setEditing(null);
      setName(""); setUrl("");
      // Reset override → next render will display `nextPriority()` based on
      // the freshly-loaded `items.length`, i.e. "next free slot".
      setPriority(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (bu) => {
    try {
      await api.delete(`/business-units/${bu.business_unit_id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      logger.error("Delete business unit failed:", err);
      toast.error("Failed");
    }
  };

  const refreshOg = async (bu) => {
    setRefreshingId(bu.business_unit_id);
    try {
      const { data } = await api.post(`/business-units/${bu.business_unit_id}/refresh-og-image`);
      if (data.og_image_url) toast.success("Image refreshed");
      else toast.error("Couldn't fetch an Open Graph image for this URL");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div>
            <div className="label-overline">Add property</div>
            <h2 className="font-display font-bold text-xl tracking-tight">
              {editing ? "Edit property" : "Add a website / property"}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              Each website tracked separately. Priority controls the order in the PDF report (1 = most important).
            </p>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_120px_auto] gap-3 items-end">
            <div className="space-y-2">
              <Label className="label-overline">Name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. GB Lifts" className="rounded-sm h-10"
                data-testid="bu-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Website URL</Label>
              <Input required value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.example.com" className="rounded-sm h-10"
                data-testid="bu-url-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Priority (1–10)</Label>
              <Input
                type="number" min={1} max={10} step={1}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="rounded-sm h-10 text-center font-mono"
                data-testid="bu-priority-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy}
                className="rounded-sm h-10 bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="bu-submit-button">
                {editing ? <Pencil className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                {editing ? "Save" : "Add"}
              </Button>
              {editing && (
                <Button type="button" variant="ghost" className="rounded-sm h-10"
                  onClick={() => { setEditing(null); setName(""); setUrl(""); setPriority(null); }}>Cancel</Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-overline">Properties ({items.length})</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">
            {savingOrder ? "Saving order…" : "Drag the handle to reorder priorities"}
          </div>
        </div>
        <div className="space-y-2">
          {items.map((bu) => {
            const imgSrc = bu.og_image_url ? `${BACKEND}${bu.og_image_url}` : null;
            const isDragging = dragId === bu.business_unit_id;
            const isOver = overId === bu.business_unit_id && dragId && dragId !== bu.business_unit_id;
            return (
            <React.Fragment key={bu.business_unit_id}>
            <Card
              className={`rounded-sm border-zinc-200 transition-all
                ${isDragging ? "opacity-50" : ""}
                ${isOver ? "border-[#002FA7] border-2 shadow-md" : ""}`}
              data-testid={`bu-row-${bu.business_unit_id}`}
              onDragOver={onDragOver(bu.business_unit_id)}
              onDragLeave={onDragLeave}
              onDrop={onDrop(bu.business_unit_id)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div
                  className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-600 transition-colors flex-shrink-0 -ml-1 px-1 py-2"
                  title="Drag to reorder priority"
                  data-testid={`bu-drag-handle-${bu.business_unit_id}`}
                  draggable={true}
                  onDragStart={onDragStart(bu.business_unit_id)}
                  onDragEnd={onDragEnd}
                >
                  <GripVertical className="h-5 w-5" />
                </div>
                <div className="relative h-12 w-12 rounded-sm overflow-hidden flex-shrink-0 bg-zinc-100 border border-zinc-200">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={bu.name}
                      className="h-full w-full object-cover"
                      data-testid={`bu-og-image-${bu.business_unit_id}`}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="h-full w-full bg-zinc-900 text-white flex items-center justify-center">
                      <Globe2 className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/clients/${clientId}/properties/${bu.business_unit_id}`}
                      className="font-display font-bold text-sm hover:text-[#002FA7] hover:underline"
                      data-testid={`bu-name-link-${bu.business_unit_id}`}
                      title="Open property details">
                      {bu.name}
                    </Link>
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-[#002FA7]/10 text-[#002FA7]"
                      data-testid={`bu-priority-badge-${bu.business_unit_id}`}
                      title="Property priority (1 = most important)"
                    >
                      P{bu.priority ?? 5}
                    </span>
                  </div>
                  <a href={bu.website_url} target="_blank" rel="noreferrer"
                    className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-[#002FA7]">
                    {bu.website_url} <ExternalLink className="h-3 w-3" />
                  </a>
                  <div className="mt-1.5">
                    <PlatformPills
                      keys={bu.active_platforms || []}
                      onEdit={() => setPlatformsBuId(
                        platformsBuId === bu.business_unit_id ? null : bu.business_unit_id
                      )}
                    />
                  </div>
                  <BudgetActualStrip
                    summary={summaries[bu.business_unit_id]}
                    buId={bu.business_unit_id}
                  />
                  <PropertyDomainsStrip
                    domains={domainsByBu[bu.business_unit_id]}
                    buId={bu.business_unit_id}
                  />
                </div>
                <Button
                  size="sm" variant="ghost" className="rounded-sm text-xs"
                  disabled={refreshingId === bu.business_unit_id}
                  onClick={() => refreshOg(bu)}
                  title="Re-fetch Open Graph image"
                  data-testid={`bu-refresh-og-${bu.business_unit_id}`}>
                  <RefreshCcw className={`h-3.5 w-3.5 ${refreshingId === bu.business_unit_id ? "animate-spin" : ""}`} />
                </Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-xs"
                  onClick={() => setBriefBuId(briefBuId === bu.business_unit_id ? null : bu.business_unit_id)}
                  data-testid={`bu-brief-toggle-${bu.business_unit_id}`}>
                  <NotebookText className="h-3.5 w-3.5 mr-1" /> Brief &amp; Budget
                </Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-xs"
                  onClick={() => setSeoBuId(seoBuId === bu.business_unit_id ? null : bu.business_unit_id)}
                  data-testid={`bu-seo-toggle-${bu.business_unit_id}`}
                  title="Competitors & keyphrases for SERP tracking">
                  <Search className="h-3.5 w-3.5 mr-1" /> SEO
                  {((bu.competitors?.length || 0) + (bu.keyphrases?.length || 0)) > 0 && (
                    <span className="ml-1.5 inline-flex items-center min-w-[18px] h-[18px] px-1 rounded-sm bg-[#002FA7]/10 text-[#002FA7] text-[10px] font-mono">
                      {(bu.competitors?.length || 0)}·{(bu.keyphrases?.length || 0)}
                    </span>
                  )}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-sm"
                  onClick={() => {
                    setEditing(bu);
                    setName(bu.name);
                    setUrl(bu.website_url);
                    setPriority(bu.priority ?? 5);
                  }}
                  data-testid={`edit-bu-${bu.business_unit_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                      data-testid={`delete-bu-${bu.business_unit_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {bu.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        All site-specific uploads and connections for this unit will be removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                        onClick={() => remove(bu)}
                        data-testid={`confirm-delete-bu-${bu.business_unit_id}`}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
            {briefBuId === bu.business_unit_id && (
              <BUBriefEditor businessUnitId={bu.business_unit_id}
                onClose={() => setBriefBuId(null)} />
            )}
            {platformsBuId === bu.business_unit_id && (
              <PropertyPlatformsEditor
                businessUnitId={bu.business_unit_id}
                initial={bu.active_platforms || []}
                onClose={() => setPlatformsBuId(null)}
                onSaved={() => load()}
              />
            )}
            {seoBuId === bu.business_unit_id && (
              <BUSeoEditor
                businessUnit={bu}
                onClose={() => setSeoBuId(null)}
                onSaved={() => load()}
              />
            )}
            </React.Fragment>
          );})}
          {items.length === 0 && (
            <Card className="rounded-sm border-dashed border-zinc-300">
              <CardContent className="p-8 text-center text-sm text-zinc-500">
                No properties yet. Use the form above to add your first website.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function PropertyDomainsStrip({ domains, buId }) {
  if (!domains || domains.length === 0) return null;
  const primary = domains.find((d) => d.is_primary);
  const others = domains.filter((d) => !d.is_primary);
  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const totalCost = domains.reduce((s, d) => s + (Number(d.renewal_cost) || 0), 0);
  return (
    <div className="mt-2 space-y-0.5" data-testid={`bu-domains-strip-${buId}`}>
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className="text-zinc-400 uppercase tracking-wider text-[9px] font-bold">Domains</span>
        {primary && (
          <span className="inline-flex items-center gap-1 font-mono text-zinc-800"
            data-testid={`bu-domain-primary-${buId}`}>
            <span className="px-1 rounded-sm bg-[#002FA7] text-white text-[8px] uppercase tracking-wide">primary</span>
            {primary.domain}
            {primary.renewal_cost != null && (
              <span className="text-zinc-500">· {fmt(primary.renewal_cost)}/yr</span>
            )}
          </span>
        )}
        {others.map((d) => (
          <span key={d.domain_id} className="inline-flex items-center gap-1 font-mono text-zinc-500"
            data-testid={`bu-domain-redirect-${d.domain_id}`}>
            <span className="px-1 rounded-sm bg-zinc-100 text-zinc-500 text-[8px] uppercase tracking-wide">redirect</span>
            {d.domain}
            {d.renewal_cost != null && <span>· {fmt(d.renewal_cost)}/yr</span>}
          </span>
        ))}
        {totalCost > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px]"
            data-testid={`bu-domains-total-${buId}`}>
            <span className="text-zinc-400 uppercase tracking-wider text-[9px] font-bold">Domain cost</span>
            <span className="font-mono text-emerald-700 font-bold">{fmt(totalCost)}/yr</span>
          </span>
        )}
      </div>
    </div>
  );
}

function BudgetActualStrip({ summary, buId }) {
  if (!summary) return null;
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const hasBudget = (summary.budget_total || 0) > 0;
  const hasActual = (summary.actual_total || 0) > 0;
  const hasVisits = (summary.visits || 0) > 0;
  if (!hasBudget && !hasActual && !hasVisits) return null;

  const pctOfBudget = summary.actual_vs_budget_pct;
  let utilColor = "text-zinc-500";
  if (pctOfBudget != null) {
    if (pctOfBudget > 100) utilColor = "text-rose-600";
    else if (pctOfBudget > 90) utilColor = "text-amber-600";
    else utilColor = "text-emerald-600";
  }

  const pct = summary.visits_pct_change;
  let trendIcon = <Minus className="h-3 w-3" />;
  let trendColor = "text-zinc-500";
  let trendLabel = "n/a";
  if (pct == null) {
    if (hasVisits) { trendLabel = "new"; trendColor = "text-emerald-600"; trendIcon = <TrendingUp className="h-3 w-3" />; }
  } else if (pct > 0.5) {
    trendColor = "text-emerald-600";
    trendIcon = <TrendingUp className="h-3 w-3" />;
    trendLabel = `+${pct.toFixed(1)}%`;
  } else if (pct < -0.5) {
    trendColor = "text-rose-600";
    trendIcon = <TrendingDown className="h-3 w-3" />;
    trendLabel = `${pct.toFixed(1)}%`;
  } else {
    trendLabel = `${pct.toFixed(1)}%`;
  }

  return (
    <div
      className="mt-2 flex items-center gap-3 text-[11px] flex-wrap"
      data-testid={`bu-budget-strip-${buId}`}
      title={`Latest data month: ${summary.period_month}`}
    >
      {hasBudget && (
        <span className="inline-flex items-center gap-1">
          <span className="text-zinc-400 uppercase tracking-wider text-[9px] font-bold">Budget</span>
          <span className="font-mono text-zinc-800">{fmtMoney(summary.budget_total)}</span>
        </span>
      )}
      {(hasBudget || hasActual) && (
        <span className="inline-flex items-center gap-1">
          <span className="text-zinc-400 uppercase tracking-wider text-[9px] font-bold">Actual</span>
          <span className={`font-mono ${utilColor}`}>
            {fmtMoney(summary.actual_total)}
            {pctOfBudget != null && (
              <span className="ml-1 text-[10px]">({pctOfBudget.toFixed(0)}%)</span>
            )}
          </span>
        </span>
      )}
      {hasVisits && (
        <span className="inline-flex items-center gap-1">
          <span className="text-zinc-400 uppercase tracking-wider text-[9px] font-bold">Visits</span>
          <span className="font-mono text-zinc-800">
            {Number(summary.visits || 0).toLocaleString()}
          </span>
          <span className={`inline-flex items-center gap-0.5 ${trendColor}`}
            data-testid={`bu-visits-trend-${buId}`}>
            {trendIcon}<span className="font-mono text-[10px]">{trendLabel}</span>
          </span>
        </span>
      )}
      <span className="text-zinc-300 text-[9px]">· {summary.period_month}</span>
    </div>
  );
}

